namespace InvData.Util {

    export interface IntervalSetOptions {
        oneDay?: boolean;
        mtd?: boolean;
        qtd?: boolean;
        ytd?: boolean;
        fiscalYtd?: boolean;

        /**
         * Create intervals going through as-of-day and back-dating the calendar month (if the as-of-day is mid-month, the partial month will count as a single month).
         */
        sinceMonthsBack?: number[];

        /**
         * Create intervals going through as of day and back 12 months (if as of day is mid-month, the 12th month will be a partial month for the
         * final year).
         */
        sinceYearsBack?: number[];

        sinceDayIds?: number[];
        sinceInception?: boolean;
        sinceFirstFullMonth?: boolean;
        sinceFirstFullQuarter?: boolean;


        /**
         * Create for intervals for the individual calendar months preceeding current date, starting with the month before current date.
         */
        consecutiveCalendarMonthsBack?: number;

        /**
         * Create for intervals for the individual calendar quarters preceeding current date, starting with the quarter before current date.
         */
        consecutiveCalendarQuartersBack?: number;

        /**
         * Create for intervals for the individual calendar years preceeding current date, starting with the year before current date.
         */
        consecutiveCalendarYearsBack?: number;

        /**
         * Create for intervals for the individual fiscal years preceeding current date, starting with the fiscal year before current date.
         */
        consecutiveFiscalYearsBack?: number;

        /**
         * For consecutive intervals, if the as of date matches the end of a period, include that interval in the consecutive interval collection.
         * By default, the consecutive intervals start going back from the end of the prior complete interval.
         */
        includeFullFinalIntervalForConsecutiveIntervals?: boolean;

        /**
         * Create rolling intervals spanning a certain number of months.
         */
        rollingIntervals?: IntervalSetRollingInterval[];

        /**
         * Create daily intervals going back from the to date.
         */
        daily?: IntervalSetDaily;

        /**
         * Create intervals starting at some point in the past and rolling towards the as-of-date.
         */
        sinceDateCumulativeIntervals?: IntervalSetSinceDateCumulativeInterval[];
    }

    export interface IntervalSetRollingInterval {
        intervalCount?: number;
        monthSpan?: number;
        includeFinalPartialMonth?: boolean
        endToEnd?: boolean;
    }

    export interface IntervalSetDaily {
        daysBack?: number;        
    }

    export interface IntervalSetSinceDateCumulativeInterval {
        sinceDayId?: number;

        /**
         * Create the intervals going forward starting with this from date (defaults to sinceDayId, but must be specified
         * for since inception intervals).
         */
        startingAtDayId?: number;

        advanceBy?: "Month" | "Year" | "Quarter";
    }


    export interface IntervalSetResult {
        codeToIntervalInfoMap: { [code: string]: IntervalInfo };
        intervalRequests: IntervalRequestData[];
    }

    export interface IntervalRequestData {
        code: string;
        fromDayId: number;
        toDayId: number;
        isSinceFirstFullMonth?: boolean;
        isSinceFirstFullQuarter?: boolean;
    }

    export interface IntervalInfo {
        label: string;
        timeRangeDescription?: string;
        intervalRequest: IntervalRequestData;
    }

    export class IntervalSetGenerator {

        public static getIntervalSet(options: IntervalSetOptions, asOfDayId?: number): IntervalSetResult {
            if (!options) {
                options = {};
            }

            if (!asOfDayId) {
                asOfDayId = InvData.DayId.today();
            }
            
            let asOfDate = InvData.DayId.toDate(asOfDayId);

            let currentMonthEndDayId = InvData.DayId.forDate(asOfDate, InvData.RelativeDate.MonthEnd);
            let priorMonthEndDayId = InvData.DayId.forDate(asOfDate, InvData.RelativeDate.PriorMonthEnd);
            let priorQuarterEndDayId = InvData.DayId.forDate(asOfDate, InvData.RelativeDate.PriorQuarterEnd);
            let priorYearEndDayId = InvData.DayId.forDate(asOfDate, InvData.RelativeDate.PriorYearEnd);

            let isAsOfDayOnMonthEnd = (asOfDayId === currentMonthEndDayId);

            let fiscalYearStartMonth = 1;
            if ((window as any).Solovis) {
                let solovisAny = (window as any).Solovis as any;
                if (solovisAny && solovisAny.Custom && solovisAny.Custom.ModelDefaults && solovisAny.Custom.ModelDefaults.fiscalYearStartMonth) {
                    fiscalYearStartMonth = solovisAny.Custom.ModelDefaults.fiscalYearStartMonth;
                }
            }

            let codeToIntervalInfoMap: { [code: string]: IntervalInfo } = {};
            let intervalRequests = [];

            let saveInterval = (interval: IntervalInfo) => {
                if (interval.intervalRequest.fromDayId) {
                    interval.timeRangeDescription = "Interval goes from after end of "
                        + InvData.DayId.format(interval.intervalRequest.fromDayId)
                        + " through "
                        + InvData.DayId.format(interval.intervalRequest.toDayId)
                        + ".";
                }
                else {
                    interval.timeRangeDescription = "Interval goes from inception through "                        
                        + InvData.DayId.format(interval.intervalRequest.toDayId)
                        + ".";
                }

                if (!codeToIntervalInfoMap[interval.intervalRequest.code]) {
                    codeToIntervalInfoMap[interval.intervalRequest.code] = interval;
                    intervalRequests.push(interval.intervalRequest);
                }
            };

            if (options.oneDay) {
                saveInterval({
                    label: "1-Day",
                    intervalRequest: {
                        code: "OneDay",
                        fromDayId: asOfDayId - 1,
                        toDayId: asOfDayId
                    }
                });
            }

            if (options.mtd) {
                saveInterval({
                    label: "MTD",
                    intervalRequest: {
                        code: "MTD",
                        fromDayId: priorMonthEndDayId,
                        toDayId: asOfDayId
                    }
                });
            }

            if (options.qtd) {
                saveInterval({
                    label: "QTD",
                    intervalRequest: {
                        code: "QTD",
                        fromDayId: priorQuarterEndDayId,
                        toDayId: asOfDayId
                    }
                });
            }

            if (options.ytd) {
                saveInterval({
                    label: "YTD",
                    intervalRequest: {
                        code: "YTD",
                        fromDayId: priorYearEndDayId,
                        toDayId: asOfDayId
                    }
                });
            }

            if (options.fiscalYtd) {                
                let intervalFromDayId = InvData.Util.DateIndexHelper.getDateIndexOfLastDayInPreviousFiscalYear(asOfDate, fiscalYearStartMonth);

                saveInterval({
                    label: "FYTD",
                    intervalRequest: {
                        code: "FYTD",
                        fromDayId: intervalFromDayId,
                        toDayId: asOfDayId
                    }
                });
            }

            if (options.sinceMonthsBack) {
                let sinceMonthsBackSeenSet: any = {};
                for (let monthsBack of options.sinceMonthsBack) {
                    // Only include each monthsBack once.
                    if (!sinceMonthsBackSeenSet[monthsBack]) {
                        sinceMonthsBackSeenSet[monthsBack] = true;

                        let intervalFromDayId = InvData.DayId.forDateIndex(priorMonthEndDayId, InvData.RelativeDate.MonthEnd, (-1 * monthsBack) + 1);

                        saveInterval({
                          label: "" + monthsBack + "M",
                            intervalRequest: {
                                code: "SinceMonthsBack_" + monthsBack,
                                fromDayId: intervalFromDayId,
                                toDayId: asOfDayId
                            }
                        });
                    }
                }
            }

            if (options.sinceYearsBack) {
                let sinceYearsBackSeenSet: any = {};
                for (let yearsBack of options.sinceYearsBack) {
                    // Only include each yearsBack once.
                    if (!sinceYearsBackSeenSet[yearsBack]) {
                        sinceYearsBackSeenSet[yearsBack] = true;

                        let intervalFromDayId = InvData.DayId.forDateIndex(priorMonthEndDayId, InvData.RelativeDate.MonthEnd, (-12 * yearsBack) + 1);

                        saveInterval({
                            label: "" + yearsBack + "Y",
                            intervalRequest: {
                                code: "SinceYearsBack_" + yearsBack,
                                fromDayId: intervalFromDayId,
                                toDayId: asOfDayId
                            }
                        });
                    }
                }
            }

            if (options.sinceDayIds) {
                let sinceDayIdSeenSet: any = {};
                for (let sinceDayId of options.sinceDayIds) {
                    // Only include each since day id once.
                    if (!sinceDayIdSeenSet[sinceDayId]) {
                        sinceDayIdSeenSet[sinceDayId] = true;

                        let sinceDateStr = InvData.DayId.format(sinceDayId);

                        saveInterval({
                            label: "Since " + sinceDateStr,
                            intervalRequest: {
                                code: "SinceDay_" + sinceDayId,
                                fromDayId: sinceDayId,
                                toDayId: asOfDayId
                            }          
                        });
                    }                    
                }
            }

            if (options.sinceInception) {
                saveInterval({
                    label: "Since Inception",
                    intervalRequest: {
                        code: "SinceInception",
                        fromDayId: null,
                        toDayId: asOfDayId
                    }
                });
            }

            if (options.sinceFirstFullMonth) {
                saveInterval({
                    label: "Since First Full Month",
                    intervalRequest: {
                        code: "SinceFirstFullMonth",
                        fromDayId: null,
                        toDayId: asOfDayId,
                        isSinceFirstFullMonth: true
                    }
                });
            }

            if (options.sinceFirstFullQuarter) {
                saveInterval({
                    label: "Since First Full Quarter",
                    intervalRequest: {
                        code: "SinceFirstFullQuarter",
                        fromDayId: null,
                        toDayId: asOfDayId,
                        isSinceFirstFullQuarter: true
                    }
                });
            }

            if (options.consecutiveCalendarMonthsBack && options.consecutiveCalendarMonthsBack > 0) {
                let lastFromDayId = priorMonthEndDayId;
                if (options.includeFullFinalIntervalForConsecutiveIntervals) {
                    let followingIntervalEndDayId = InvData.DayId.forDateIndex(lastFromDayId + 1, InvData.RelativeDate.MonthEnd);
                    if (followingIntervalEndDayId === asOfDayId) {
                        lastFromDayId = followingIntervalEndDayId;
                    }
                }

                for (let monthBackCo = 1; monthBackCo <= options.consecutiveCalendarMonthsBack; monthBackCo++) {
                    let intervalFromDayId = InvData.DayId.forDateIndex(lastFromDayId, InvData.RelativeDate.PriorMonthEnd);

                    saveInterval({
                        label: InvData.Util.DateIndexHelper.formatAsShortMonthNameYear(lastFromDayId),
                        intervalRequest: {
                            code: "CalendarMonthBack_" + monthBackCo,
                            fromDayId: intervalFromDayId,
                            toDayId: lastFromDayId
                        }
                    });

                    lastFromDayId = intervalFromDayId;
                }
            }

            if (options.consecutiveCalendarQuartersBack && options.consecutiveCalendarQuartersBack > 0) {
                let lastFromDayId = priorQuarterEndDayId;
                if (options.includeFullFinalIntervalForConsecutiveIntervals) {
                    let followingIntervalEndDayId = InvData.DayId.forDateIndex(lastFromDayId + 1, InvData.RelativeDate.QuarterEnd);
                    if (followingIntervalEndDayId === asOfDayId) {
                        lastFromDayId = followingIntervalEndDayId;
                    }
                }

                for (let quarterBackCo = 1; quarterBackCo <= options.consecutiveCalendarQuartersBack; quarterBackCo++) {
                    let intervalFromDayId = InvData.DayId.forDateIndex(lastFromDayId, InvData.RelativeDate.PriorQuarterEnd);

                    saveInterval({
                        label: InvData.Util.DateIndexHelper.formatAsQuarterYearString(lastFromDayId),
                        intervalRequest: {
                            code: "CalendarQuarterBack_" + quarterBackCo,
                            fromDayId: intervalFromDayId,
                            toDayId: lastFromDayId
                        }
                    });

                    lastFromDayId = intervalFromDayId;
                }
            }

            if (options.consecutiveCalendarYearsBack && options.consecutiveCalendarYearsBack > 0) {
                let consecutiveCalendarYearsBack = typeof (options.consecutiveCalendarYearsBack) === "number" ? options.consecutiveCalendarYearsBack : parseInt((options.consecutiveCalendarYearsBack as any) as string);

                let year = asOfDate.getFullYear();
                let startingYearBackCo = 1;
                if (options.includeFullFinalIntervalForConsecutiveIntervals) {
                    let asOfYearEndDayId = InvData.DayId.forDateIndex(asOfDayId, InvData.RelativeDate.YearEnd);
                    if (asOfYearEndDayId === asOfDayId) {
                        startingYearBackCo = 0;
                    }
                }

                let endYearCo = startingYearBackCo + consecutiveCalendarYearsBack;
                for (let yearBackCo = startingYearBackCo; yearBackCo < endYearCo; yearBackCo++) {
                    let intervalYear = year - yearBackCo;
                    
                    saveInterval({
                        label: "" + intervalYear,
                        intervalRequest: {
                            code: "CalendarYearBack_" + yearBackCo,
                            fromDayId: InvData.DayId.forDate(new Date(intervalYear - 1, 11, 31)),
                            toDayId: InvData.DayId.forDate(new Date(intervalYear, 11, 31))
                        }
                    });
                }                
            }

            if (options.consecutiveFiscalYearsBack && options.consecutiveFiscalYearsBack > 0) {
                let lastFromDayId = InvData.Util.DateIndexHelper.getDateIndexOfLastDayInPreviousFiscalYear(asOfDate, fiscalYearStartMonth);
                if (options.includeFullFinalIntervalForConsecutiveIntervals) {
                    let followingIntervalEndDayId = InvData.Util.DateIndexHelper.getDateIndexOfLastDayInFiscalYear(lastFromDayId + 1, fiscalYearStartMonth);
                    if (followingIntervalEndDayId === asOfDayId) {
                        lastFromDayId = followingIntervalEndDayId;
                    }
                }

                for (let fiscalYearBackCo = 1; fiscalYearBackCo <= options.consecutiveFiscalYearsBack; fiscalYearBackCo++) {
                    let intervalFromDayId = InvData.DayId.forDateIndex(lastFromDayId, InvData.RelativeDate.MonthEnd, -12);
                    let intervalToDate = InvData.DayId.toDate(lastFromDayId);
                    let fiscalYear = intervalToDate.getFullYear();

                    saveInterval({
                        label: "FY" + fiscalYear,
                        intervalRequest: {
                            code: "FiscalYearBack_" + fiscalYearBackCo,
                            fromDayId: intervalFromDayId,
                            toDayId: lastFromDayId
                        }
                    });

                    lastFromDayId = intervalFromDayId;
                }
            }

            if (options.rollingIntervals) {
                let monthSpanSeenSet: any = {};
                for (let rollingInterval of options.rollingIntervals) {
                    let monthSpan = rollingInterval.monthSpan ? rollingInterval.monthSpan : 36;
                    let monthSpanStr = "" + monthSpan + "M";

                    let rollingPrefix: string;
                    if (rollingInterval.endToEnd) {
                        rollingPrefix = "EndToEndRolling_";
                    }
                    else {
                        rollingPrefix = "Rolling_";
                    }

                    if (!monthSpanSeenSet[monthSpan]) {
                        monthSpanSeenSet[monthSpan] = true;

                        let nextIntervalNumber = 1;

                        let nextToDayId = priorMonthEndDayId;
                                 
                        // Add the rolling intervals.
                        if (rollingInterval.includeFinalPartialMonth || isAsOfDayOnMonthEnd) {
                            let rollingToDayId = asOfDayId;
                            let rollingFromDayId = InvData.DayId.forDateIndex(currentMonthEndDayId, RelativeDate.MonthEnd, -monthSpan);
                            let intervalNumber = nextIntervalNumber++;

                            let intervalLabel;
                            if (isAsOfDayOnMonthEnd) {
                                intervalLabel = monthSpanStr + " " + InvData.Util.DateIndexHelper.formatAsShortMonthNameYear(asOfDayId);
                            }
                            else {
                                intervalLabel = monthSpanStr + " " + InvData.Util.DateIndexHelper.formatAsShortMonthDayYear(asOfDayId);
                            }

                            saveInterval({
                                label: intervalLabel,
                                intervalRequest: {
                                    code: rollingPrefix + monthSpan + "M_" + intervalNumber,
                                    fromDayId: rollingFromDayId,
                                    toDayId: rollingToDayId
                                }
                            });

                            if (rollingInterval.endToEnd) {
                                nextToDayId = rollingFromDayId;
                            }
                        }                        

                        if (rollingInterval.intervalCount > 1) {
                            while (nextIntervalNumber <= rollingInterval.intervalCount) {
                                let rollingToDayId = nextToDayId;
                                let rollingFromDayId = InvData.DayId.forDateIndex(rollingToDayId, RelativeDate.MonthEnd, -monthSpan);
                                let intervalNumber = nextIntervalNumber++;

                                let intervalLabel = monthSpanStr + " " + InvData.Util.DateIndexHelper.formatAsShortMonthNameYear(rollingToDayId);
                            
                                saveInterval({
                                    label: intervalLabel,
                                    intervalRequest: {
                                        code: rollingPrefix + monthSpan + "M_" + intervalNumber,
                                        fromDayId: rollingFromDayId,
                                        toDayId: rollingToDayId
                                    }
                                });

                                if (rollingInterval.endToEnd) {
                                    nextToDayId = rollingFromDayId;
                                }
                                else {
                                    nextToDayId = InvData.DayId.forDateIndex(nextToDayId, RelativeDate.PriorMonthEnd);
                                }
                            }
                        }
                    }   
                }
            }

            if (options.daily && options.daily.daysBack) {
                for (let daysBackCo = 0; daysBackCo < options.daily.daysBack; daysBackCo++) {
                    let currentDayId = asOfDayId - daysBackCo;
                    saveInterval({
                        label: InvData.DayId.format(currentDayId),
                        intervalRequest: {
                            code: "Daily_" + daysBackCo,
                            fromDayId: currentDayId - 1,
                            toDayId: currentDayId
                        }
                    });
                }
            }
            
            if (options.sinceDateCumulativeIntervals) {
                // Add intervals that start at some date in past where "To" date steadily advances towards as-of-date.
                for (let sinceDateCumulativeInterval of options.sinceDateCumulativeIntervals) {                             
                    let advanceDayId = (toDayId) => {
                        let nextToDayId = toDayId;

                        if (sinceDateCumulativeInterval.advanceBy === "Month") {
                            let currentMonthEndDayId = InvData.DayId.forDateIndex(nextToDayId, RelativeDate.MonthEnd);
                            if (currentMonthEndDayId === currentToDayId) {
                                nextToDayId = InvData.DayId.forDateIndex(currentMonthEndDayId, RelativeDate.MonthEnd, 1);
                            }
                            else {
                                nextToDayId = currentMonthEndDayId;
                            }
                        }
                        else if (sinceDateCumulativeInterval.advanceBy === "Quarter") {
                            let currentQuarterEndDayId = InvData.DayId.forDateIndex(nextToDayId, RelativeDate.QuarterEnd);
                            if (currentQuarterEndDayId === currentToDayId) {
                                nextToDayId = InvData.DayId.forDateIndex(currentQuarterEndDayId, RelativeDate.QuarterEnd, 1);
                            }
                            else {
                                nextToDayId = currentQuarterEndDayId;
                            }
                        }
                        else {
                            // We only support Month and Year, so fallback to Year if invalid value appears here.
                            let currentYearEndDayId = InvData.DayId.forDateIndex(nextToDayId, RelativeDate.YearEnd);
                            if (currentYearEndDayId === currentToDayId) {
                                nextToDayId = InvData.DayId.forDateIndex(currentYearEndDayId, RelativeDate.YearEnd, 1);
                            }
                            else {
                                nextToDayId = currentYearEndDayId;
                            }
                        }

                        if (nextToDayId > asOfDayId) {
                            nextToDayId = asOfDayId;
                        }

                        return nextToDayId;
                    };

                    let currentToDayId;
                    if (sinceDateCumulativeInterval.startingAtDayId) {
                        currentToDayId = sinceDateCumulativeInterval.startingAtDayId;
                    }
                    else {                        
                        currentToDayId = advanceDayId(sinceDateCumulativeInterval.sinceDayId); 
                    }

                    let intervalLabelPrefix: string;
                    let intervalCodePrefix: string;
                    if (sinceDateCumulativeInterval.sinceDayId) {
                        intervalLabelPrefix = "Since " + InvData.DayId.format(sinceDateCumulativeInterval.sinceDayId) + " To ";
                        intervalCodePrefix = "SinceDateCumulative_" + sinceDateCumulativeInterval.sinceDayId + "_";
                    }
                    else {
                        intervalLabelPrefix = "Since Inception To ";
                        intervalCodePrefix = "SinceDateCumulative_Inception_";
                    }

                    let intervalsDone = false;
                    while (currentToDayId <= asOfDayId && !intervalsDone) {
                        if (sinceDateCumulativeInterval.sinceDayId !== currentToDayId) {
                            saveInterval({
                                label: intervalLabelPrefix + InvData.DayId.format(currentToDayId),
                                intervalRequest: {
                                    code: intervalCodePrefix + currentToDayId,
                                    fromDayId: sinceDateCumulativeInterval.sinceDayId,
                                    toDayId: currentToDayId
                                }
                            });
                        }

                        if (currentToDayId >= asOfDayId) {
                            // We have all of our intervals.
                            intervalsDone = true;
                        }
                        else {
                            currentToDayId = advanceDayId(currentToDayId);                            
                        }
                    }
                }
            }

            let result = {
                codeToIntervalInfoMap: codeToIntervalInfoMap,
                intervalRequests: intervalRequests
            };

            return result;
        }

    }

}